import React, { useCallback, useMemo, useRef } from 'react';
import { css } from '@emotion/css';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { UploadFill } from '@lyearn/icons';
import { EmptyState } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { LinkButton, Stack, Typography } from '@lyearn/ui';
import { listContainerStyles } from '@/components/ContentList/components/Items/styles';
import { parseVisibilitySettingsRestrictionConfig } from '@/components/Form/components/VisibilitySettings/utils';
import Loader from '@/components/Loader';
import { SearchBarWithEmptyStateAdornment } from '@/components/SearchBar';
import SearchBarWithCount from '@/components/SearchBar/SearchBarWithCount';
import VirtualizedList from '@/components/VirtualizedList';
import useDebounceState from '@/hooks/useDebounceState';
import BulkImport from '@/modules/corporate/modules/bulk-import';
import BulkImportDialog from '@/modules/corporate/modules/bulk-import/components/BulkImportDialog';
import { JobStateFragment } from '@/modules/corporate/modules/bulk-import/graphql/fragments/generated/JobState';
import useImportProps from '@/modules/corporate/modules/bulk-import/hooks/useImportProps';
import ListItem from '@/modules/corporate/modules/group/components/GroupListItem';
import { getGroupBulkImportUpdateItems } from '@/modules/corporate/modules/group/views/GroupManager/utils/getGroupBulkImportUpdateItems';
import { JobPurposeEnum, VisibilitySettingsRestrictionTypeEnum } from '@/types/schema';

import useRestrictedVisibilitySettingsGroupsTab from './hooks/useRestrictedVisibilitySettingsGroupsTab';
import { RestrictedVisibilitySettingsGroupsTabType } from './types';

const CLASSES = {
  container: css`
    padding-inline-end: 0.8rem !important;
    padding-inline-start: 0.8rem !important;
  `,
};

const TEMPLATE_FILE_NAME = 'import announcement groups sample';

const RestrictedVisibilitySettingsGroupsTab: React.FC<RestrictedVisibilitySettingsGroupsTabType> = (
  props,
) => {
  const { t } = useTranslation();
  const { contextualGroupsConfig, disabled, value, onChange, allowCsvImport } = props;

  const [searchText, setSearchText, debounceSearchText] = useDebounceState('');
  const [showLearnerImportDialog, { on, off }] = useToggle(false);

  const lastCompletedJobId = useRef<string | null>(null);

  const {
    groups,
    isGroupsFetching,
    fetchMoreGroups,
    toggleSelectedContextualGroupIds,
    selectedGroupIdsSet,
    selectedContextualGroupsSet,
    toggleSelectedGroupIds,
    bulkImportGroups,
  } = useRestrictedVisibilitySettingsGroupsTab({
    contextualGroupsConfig,
    disabled,
    value,
    onChange,
    debounceSearchText,
    searchText,
  });

  const { contextualGroups, groupIds } = useMemo(
    () => parseVisibilitySettingsRestrictionConfig(value),
    [value],
  );

  const onSelect = useCallback(
    (groupId: string, contextualGroupId?: VisibilitySettingsRestrictionTypeEnum) => {
      if (contextualGroupId) {
        toggleSelectedContextualGroupIds(contextualGroupId);

        return;
      }
      toggleSelectedGroupIds(groupId);
    },
    [toggleSelectedContextualGroupIds, toggleSelectedGroupIds],
  );

  const { fileInputRef, showFileSelector, uppy, currentImportJob, refetchJobs } = useImportProps({
    purpose: JobPurposeEnum.UserGroupImport,
  });

  const onImportJobComplete = useCallback(
    (job: JobStateFragment) => {
      if (job.id === lastCompletedJobId.current) {
        return;
      }

      lastCompletedJobId.current = job.id;
      const importedGroupIds = job.meta?.userGroupImportMetadata?.groupIds;

      if (importedGroupIds?.length) {
        bulkImportGroups(importedGroupIds);
      }
    },
    [bulkImportGroups, selectedGroupIdsSet],
  );

  const renderer = useCallback(() => {
    if (isGroupsFetching) {
      return <Loader />;
    }

    if (!groups.length) {
      if (searchText) {
        return (
          <EmptyState
            className="flex-1"
            description={t('Group:Modal.SelectGroupsEmptyDescription', { text: searchText })}
            title={t('Group:Modal.SelectGroupsEmptyTitle')}
          />
        );
      }

      return (
        <EmptyState
          className="flex-1"
          description={t('Group:Modal.NoGroupsDescription')}
          title={t('Group:Modal.NoGroupsAvailable')}
        />
      );
    }

    return (
      <VirtualizedList
        className={cn(listContainerStyles, 'flex-1')}
        data={groups}
        endReached={fetchMoreGroups}
        itemContent={(index) => {
          const group = groups[index];

          return (
            <ListItem
              key={group.id}
              classes={CLASSES}
              group={group}
              selected={
                group.type === 'ContextualUserGroupType'
                  ? selectedContextualGroupsSet.has(group.id)
                  : selectedGroupIdsSet.has(group.id)
              }
              onSelect={onSelect}
            />
          );
        }}
      />
    );
  }, [
    fetchMoreGroups,
    groups,
    isGroupsFetching,
    onSelect,
    searchText,
    selectedContextualGroupsSet,
    selectedGroupIdsSet,
    currentImportJob,
    fileInputRef,
    onImportJobComplete,
    uppy,
    t,
  ]);

  return (
    <>
      <Stack className="pb-12 pe-16 ps-24">
        {allowCsvImport ? (
          <Stack className="w-full" direction="col">
            <SearchBarWithEmptyStateAdornment
              endAdornment={
                <LinkButton
                  className="!w-[18rem]"
                  startIcon={<UploadFill height={20} width={20} />}
                  onClick={on}>
                  <Typography className="-me-16" numberOfLines={1} variant="label-03">
                    {t('Common:BulkImport.UploadCsv')}
                  </Typography>
                </LinkButton>
              }
              placeholder={t('Common:Search')}
              shape="pill"
              value={searchText}
              onChange={setSearchText}
            />
            <BulkImport
              currentImportJob={currentImportJob}
              failedSnackbarMessage={t('Group:BulkImport.GroupImportFailed')}
              fileInputRef={fileInputRef}
              jobPurpose={[JobPurposeEnum.UserGroupImport]}
              refetchJobs={refetchJobs}
              updateDataDialogProps={{ getUpdateItems: getGroupBulkImportUpdateItems }}
              uppy={uppy}
              onComplete={onImportJobComplete}
              onImportStatusUpdate={() => {}}
            />
            <Typography className="mt-16 ms-8" color="text-text-placeholder" variant="label-caps">
              {t('Common:SelectedCount', {
                count: contextualGroups.length + groupIds.length,
              })}
            </Typography>
          </Stack>
        ) : (
          <SearchBarWithCount
            placeholder={t('Common:Search')}
            selectedCount={contextualGroups.length + groupIds.length}
            shape="square"
            value={searchText}
            onChange={setSearchText}
          />
        )}
      </Stack>
      {renderer()}
      {allowCsvImport && showLearnerImportDialog ? (
        <BulkImportDialog
          description={t('Admin:BulkImport.BulkImportAnnouncementGroupsDescription')}
          jobPurpose={JobPurposeEnum.UserGroupImport}
          templateFileName={TEMPLATE_FILE_NAME}
          title={t('Admin:BulkImport.ImportGroups')}
          onClose={off}
          onSubmit={showFileSelector}
        />
      ) : null}
    </>
  );
};

export default RestrictedVisibilitySettingsGroupsTab;
